import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, MapPin } from "lucide-react";
import { PROJECTS } from "@/lib/data";
import { useContent } from "@/lib/api";

const FILTERS = ['All', 'Residential', 'Master-Planned', 'Entitlement'];

export const Projects = () => {
  const projects = useContent("projects", PROJECTS);
  const [filter, setFilter] = useState('All');
  const [active, setActive] = useState(0);

  const items = useMemo(
    () => (filter === 'All' ? projects : projects.filter(p => p.category === filter)),
    [filter, projects]
  );

  return (
    <section id="projects" className="py-24 lg:py-40 bg-paper">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12">
          <div className="max-w-2xl">
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-clay">The portfolio</span>
            <h2 className="font-display text-3xl lg:text-5xl text-ink mt-3 leading-[1.05] tracking-[-0.02em] text-balance">
              Filter by <em className="font-light">discipline.</em>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map(f => (
              <button
                key={f}
                onClick={() => { setFilter(f); setActive(0); }}
                className={`mono text-[10px] uppercase tracking-[0.22em] px-4 py-2 border transition-colors ${
                  filter === f
                    ? 'bg-ink text-paper border-ink'
                    : 'bg-transparent text-foreground border-border hover:border-ink'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Featured project (large) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 mb-10">
          <div className="lg:col-span-7 relative aspect-[16/11] overflow-hidden group">
            <AnimatePresence mode="wait">
              <motion.img
                key={items[active]?.image}
                src={items[active]?.image}
                alt={items[active]?.title}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8 }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute top-4 left-4 mono text-[10px] uppercase tracking-[0.22em] text-paper bg-ink/60 backdrop-blur px-3 py-1.5">
              {items[active]?.category} · {items[active]?.year}
            </div>
          </div>
          <div className="lg:col-span-5 flex flex-col">
            <AnimatePresence mode="wait">
              <motion.div
                key={items[active]?.title}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex items-center gap-2 text-muted-foreground mono text-[11px] uppercase tracking-[0.2em]">
                  <MapPin className="h-3.5 w-3.5" /> {items[active]?.location}
                </div>
                <h3 className="font-display text-4xl lg:text-5xl text-ink mt-3 leading-[1.05] tracking-[-0.02em]">
                  {items[active]?.title}
                </h3>
                <p className="mt-4 text-base text-foreground/80 leading-relaxed">
                  {items[active]?.blurb}
                </p>
                <dl className="mt-8 grid grid-cols-3 gap-4 border-t border-border pt-6">
                  <div>
                    <dt className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Acres</dt>
                    <dd className="font-display text-2xl lg:text-3xl text-ink mt-1">{items[active]?.acres}</dd>
                  </div>
                  <div>
                    <dt className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Scale</dt>
                    <dd className="font-display text-2xl lg:text-3xl text-ink mt-1">{items[active]?.lots}</dd>
                  </div>
                  <div>
                    <dt className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Status</dt>
                    <dd className="font-display text-2xl lg:text-3xl text-ink mt-1">{items[active]?.year}</dd>
                  </div>
                </dl>
              </motion.div>
            </AnimatePresence>

            <div className="mt-auto pt-10">
              <Link to="/contact-us" className="inline-flex items-center gap-2 mono text-[11px] uppercase tracking-[0.22em] text-clay group">
                Inquire about this program
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Thumbnail strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {items.map((p, i) => (
            <button
              key={p.title}
              onClick={() => setActive(i)}
              className={`group relative aspect-[4/5] overflow-hidden text-left ${
                i === active ? 'ring-1 ring-clay' : 'opacity-80 hover:opacity-100'
              }`}
            >
              <img src={p.image} alt={p.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3">
                <div className="mono text-[9px] uppercase tracking-[0.2em] text-paper/70">{p.category}</div>
                <div className="font-display text-paper text-base leading-tight mt-0.5">{p.title}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
